<script setup lang="ts">
import type { Dictionary, ExportFormat } from "@/types";

import { storeToRefs } from "pinia";
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useDisplay } from "vuetify";

import VcConfirm from "@/components/VcConfirm.vue";
import VcDictionaryImportFromGuildDialog from "@/components/VcDictionaryImportFromGuildDialog.vue";

import { useDictionaryStore } from "@/stores/useDictionaryStore";
import { useGuildStore } from "@/stores/useGuildStore";
import { useLoadingStore } from "@/stores/useLoadingStore";
import { useVGuildStore } from "@/stores/useVGuildStore";

const { mobile } = useDisplay();
const route = useRoute();

const confirmDeleteRef = ref<InstanceType<typeof VcConfirm> | null>(null);

const loadingStore = useLoadingStore();
const dictionaryStore = useDictionaryStore();
const guildStore = useGuildStore();
const vGuildStore = useVGuildStore();

const { dictionaries } = storeToRefs(dictionaryStore);
const { guild } = storeToRefs(guildStore);
const { vGuild } = storeToRefs(vGuildStore);

const showMember = computed(() => dictionaries.value.some((d) => d.user));

const displayDictionaries = computed(() => {
  const filteredDictionaries = dictionaries.value?.filter(({ before, after }) => {
    return (
      (searchBefore.value && before?.includes(searchText.value)) ||
      (searchAfter.value && after?.includes(searchText.value))
    );
  });
  if (!filteredDictionaries) return [];

  return filteredDictionaries.sort((x: Dictionary, y: Dictionary) => {
    return x.before > y.before ? 1 : x.before < y.before ? -1 : 0;
  });
});

const searchText = ref("");
const searchBefore = ref(true);
const searchAfter = ref(true);
const selectedIds = ref<(string | bigint)[]>([]);
const dialog = ref(false);
const currentDictionary = ref<Dictionary | undefined>(undefined);
const importDialogOpen = ref(false);
const importFromGuildDialogOpen = ref(false);
const importFile = ref<File | null>(null);
const canAddDictionary = computed(() => {
  const limit = vGuildStore.boostCount === 0 ? 50 : 300;
  return !(dictionaries.value.length < limit);
});

const onDelete = computed(() => selectedIds.value.length > 0);
const selectedState = computed({
  get() {
    return onDelete.value;
  },
  set() {
    if (selectedIds.value.length === displayDictionaries.value.length) {
      selectedIds.value = [];
    } else {
      selectedIds.value = displayDictionaries.value.map(({ id }) => id);
    }
  },
});

const updateReachedLimit = () => {
  // `canAddDictionary` is now a computed based on `dictionaries` and `vGuildStore.boostCount`,
  // so this function no longer needs to update any state. It is kept for backward compatibility.
  void canAddDictionary.value;
};

const toggleSelect = (dictionary: Dictionary) => {
  const index = selectedIds.value.indexOf(dictionary.id);
  if (index >= 0) {
    selectedIds.value.splice(index, 1);
  } else {
    selectedIds.value.push(dictionary.id);
  }
};
const openDialog = (dictionary?: Dictionary) => {
  currentDictionary.value = dictionary;
  dialog.value = true;
};
const onClickItem = (dictionary: Dictionary) => {
  onDelete.value ? toggleSelect(dictionary) : openDialog(dictionary);
};
const save = async ({ before, after }: { before: string; after: string }) => {
  if (currentDictionary.value) {
    await dictionaryStore.update({ id: currentDictionary.value.id, before, after });
  } else {
    await dictionaryStore.create({ before, after });
  }
  dialog.value = false;
  updateReachedLimit();
};
const deleteSelected = async () => {
  const success = await dictionaryStore.delete(selectedIds.value);

  if (success) {
    selectedIds.value = [];
  }
};
const openImport = (file: File) => {
  importFile.value = file;
  importDialogOpen.value = true;
};
const openImportFromGuild = () => {
  importFromGuildDialogOpen.value = true;
};
const onExportSelected = (format: ExportFormat) => {
  const orderedIds = displayDictionaries.value
    .map(({ id }) => id)
    .filter((id) => selectedIds.value.includes(id));
  dictionaryStore.exportSelectedDictionaries(orderedIds, format);
};
const onExport = (format: ExportFormat) => {
  dictionaryStore.exportDictionary(format);
};

watch(vGuild, async () => {
  if (!route.path.match(/\/server\/\d+\/dictionary/)) return;

  updateReachedLimit();
  if (vGuild.value.guild_id !== "0") {
    loadingStore.setIsGuildChangeLoading(true);
    selectedIds.value = [];
    await dictionaryStore.fetchFromVGuild();
  }
  loadingStore.setIsGuildChangeLoading(false);
});
watch(guild, () => {
  if (vGuild.value.guild_id === "0") loadingStore.setIsGuildChangeLoading(false);
});
</script>

<template>
  <VcGuildInvite v-if="guild?.status === 'managed_not_joined'" />
  <div
    v-if="vGuild && vGuild.id !== '0'"
    class="w-100 d-flex flex-column p-dictionary"
    :class="{ 'is-mobile': mobile }"
  >
    <div class="p-dictionary__header w-100 bg-background">
      <VcDictionaryPcHeader
        v-if="!mobile"
        v-model:search-text="searchText"
        v-model:search-before="searchBefore"
        v-model:search-after="searchAfter"
        :on-delete="onDelete"
        :selected-count="selectedIds.length"
        :total-count="displayDictionaries.length"
        :add-disabled="canAddDictionary"
        @open-dialog="openDialog()"
        @open-delete-confirm="confirmDeleteRef?.open()"
        @cancel-selection="selectedIds = []"
        @export="onExport"
        @export-selected="onExportSelected"
        @open-import="openImport"
        @open-import-from-guild="openImportFromGuild"
      />
      <VcDictionaryImportExportBar
        v-if="mobile"
        :on-delete="onDelete"
        :selected-count="selectedIds.length"
        :total-count="displayDictionaries.length"
        :add-disabled="canAddDictionary"
        @open-dialog="openDialog()"
        @open-delete-confirm="confirmDeleteRef?.open()"
        @cancel-selection="selectedIds = []"
        @export="onExport"
        @export-selected="onExportSelected"
        @open-import="openImport"
        @open-import-from-guild="openImportFromGuild"
      />
      <VcDictionaryTableHead
        v-model="selectedState"
        :show-member="showMember"
      />
    </div>

    <div class="p-dictionary__body">
      <v-list-item
        v-for="dictionary in displayDictionaries"
        :key="dictionary.id.toString()"
        class="p-dictionary__row pa-0"
        @click="onClickItem(dictionary)"
      >
        <VcDictionaryRow
          :dictionary="dictionary"
          :is-selected="selectedIds.includes(dictionary.id)"
          :show-member="showMember"
          @toggle="toggleSelect(dictionary)"
        />
      </v-list-item>
    </div>

    <VcDictionaryFormDialog
      v-model="dialog"
      :current-dictionary="currentDictionary"
      :dictionaries="dictionaries"
      @save="save"
    />

    <VcDictionaryImportDialog
      v-model="importDialogOpen"
      :raw-file="importFile"
    />

    <VcDictionaryImportFromGuildDialog v-model="importFromGuildDialogOpen" />

    <VcConfirm
      ref="confirmDeleteRef"
      title="削除確認"
      :message="`選択した${selectedIds.length}件を削除します。この操作は取り消せません。`"
      :actions="[{ name: 'deleteSelected', color: 'danger', text: '削除する' }]"
      @delete-selected="deleteSelected"
    />
  </div>
</template>

<style lang="scss">
.p-dictionary {
  position: relative;
  height: calc(100dvh - var(--v-layout-top));
  overflow: hidden;
}

.p-dictionary__pc-header {
  height: 120px;
  width: 100%;
  display: flex;
  align-items: center;
  padding: 0 16px;
}

.p-dictionary__search {
  width: 50%;
  max-width: 50%;
}

.p-dictionary__search-target {
  width: 80px;
  max-width: 80px;
  margin: -16px 0;
}

.p-dictionary__checkcell {
  height: 48px;
  min-width: 48px;
  width: 48px;
  display: flex;
  justify-content: center;
  align-items: center;

  .v-selection-control {
    justify-content: center;
    flex: 1;
  }
}

.p-dictionary__cell {
  height: 48px;
  line-height: 48px;
  padding: 0 8px;
  white-space: nowrap;
  overflow-x: hidden;
  text-overflow: ellipsis;

  &.is-word {
    flex: 1;
  }

  &.is-user {
    min-width: 180px;
    width: 180px;

    .is-mobile & {
      min-width: 60px;
      width: 60px;
    }
  }
}

.p-dictionary__cell-user {
  min-width: 180px;
  width: 180px;

  .is-mobile & {
    min-width: 60px;
    width: 60px;
  }
}

.p-dictionary__cell-user_name {
  min-width: 130px;
  width: 130px;
  white-space: nowrap;
  overflow-x: hidden;
  text-overflow: ellipsis;
  text-align: right;

  .is-mobile & {
    min-width: 80px;
    width: 80px;
  }
}

.p-dictionary__row {
  border-bottom: 1px solid #555555;
}

.p-dictionary__header {
  flex-shrink: 0;
  width: 100%;
}

.p-dictionary__body {
  overflow-x: hidden;
  overflow-y: scroll;
  flex: 1;
  padding-bottom: calc(100dvh - 288px);
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.p-dictionary__dialog {
  width: 720px;

  &.is-mobile {
    width: auto;
  }
}
</style>
